import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { leadFormsApi } from '../api/leadFormsApi';
import { FormFieldApiInput, LeadFormInput } from '../types/leadForms';

const OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
  staleTime: 60 * 1000,
} as const;

export const useLeadForms = () =>
  useQuery({
    queryKey: ['lead-forms'],
    queryFn: leadFormsApi.getForms,
    ...OPTIONS,
  });

export const useLeadForm = (id: string | undefined) =>
  useQuery({
    queryKey: ['lead-forms', id],
    queryFn: () => leadFormsApi.getForm(id as string),
    enabled: Boolean(id),
    ...OPTIONS,
  });

export const useFormSubmissions = (
  formId?: string
) =>
  useQuery({
    queryKey: [
      'lead-forms',
      formId,
      'submissions'
    ],
    queryFn: () =>
      leadFormsApi
        .getSubmissions(formId!)
        .then((response) => response),
    enabled: Boolean(formId),
  });

export function useLeadFormMutations() {
  const queryClient = useQueryClient();

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: ['lead-forms'],
    });

  const onSuccess = (message: string) => {
    refresh();
    toast.success(message);
  };

  const onError = () => {
    toast.error('Unable to save form changes');
  };

  const createForm = useMutation({
    mutationFn: leadFormsApi.createForm,
    onSuccess: () => onSuccess('Form created'),
    onError,
  });

  const updateForm = useMutation({
    mutationFn: ({ id, data }: { id: string; data: LeadFormInput }) =>
      leadFormsApi.updateForm(id, data),
    onSuccess: () => onSuccess('Form updated'),
    onError,
  });

  const deleteForm = useMutation({
    mutationFn: leadFormsApi.deleteForm,
    onSuccess: () => onSuccess('Form deleted'),
    onError,
  });

  const createField = useMutation({
    mutationFn: ({ formId, data }: { formId: string; data: FormFieldApiInput }) =>
      leadFormsApi.createField(formId, data),
    // 
    onError,
  });

  const updateField = useMutation({
    mutationFn: ({
      formId,
      fieldId,
      data,
    }: {
      formId: string;
      fieldId: string;
      data: FormFieldApiInput;
    }) => leadFormsApi.updateField(formId, fieldId, data),
    
    onError,
  });

  const deleteField = useMutation({
    mutationFn: ({
      formId,
      fieldId,
    }: {
      formId: string;
      fieldId: string;
    }) => leadFormsApi.deleteField(formId, fieldId),
    
    onError,
  });

  const moveFieldUp = useMutation({
    mutationFn: ({
      formId,
      fieldId,
    }: {
      formId: string;
      fieldId: string;
    }) => leadFormsApi.moveFieldUp(formId, fieldId),
    
    onError,
  });

  const moveFieldDown = useMutation({
    mutationFn: ({
      formId,
      fieldId,
    }: {
      formId: string;
      fieldId: string;
    }) => leadFormsApi.moveFieldDown(formId, fieldId),
    
    onError,
  });

  return {
    createForm,
    updateForm,
    deleteForm,
    createField,
    updateField,
    deleteField,
    moveFieldUp,
    moveFieldDown,
  };
}